import { useRef, useEffect } from 'react';
import { Bold, Italic, Underline, Heading, List, ListOrdered, Eraser } from 'lucide-react';

interface WysiwygEditorProps {
  value: string;
  onChange: (html: string) => void;
  placeholder?: string;
}

/**
 * Lightweight WYSIWYG editor (contentEditable + execCommand toolbar) — no
 * third-party dependency. Output HTML is sanitized server-side before storage.
 * Avoids native dialogs (per the app's ConfirmModal rule) by offering only
 * input-free formatting commands.
 */
export function WysiwygEditor({ value, onChange, placeholder }: WysiwygEditorProps) {
  const ref = useRef<HTMLDivElement>(null);

  // Seed the editor once on mount (or when switching to a different record).
  useEffect(() => {
    if (ref.current && ref.current.innerHTML !== (value || '')) {
      ref.current.innerHTML = value || '';
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const sync = () => {
    if (ref.current) onChange(ref.current.innerHTML);
  };

  const exec = (command: string, arg?: string) => {
    ref.current?.focus();
    document.execCommand(command, false, arg);
    sync();
  };

  const Btn = ({ onClick, title, children }: { onClick: () => void; title: string; children: React.ReactNode }) => (
    <button
      type="button"
      onMouseDown={(e) => e.preventDefault()} // keep selection/focus in the editor
      onClick={onClick}
      title={title}
      className="p-1.5 rounded text-theme-secondary hover:bg-subtle"
    >
      {children}
    </button>
  );

  return (
    <div className="border border-line rounded-lg overflow-hidden">
      <div className="flex items-center gap-0.5 border-b border-line bg-subtle px-1.5 py-1">
        <Btn onClick={() => exec('bold')} title="Bold"><Bold size={16} /></Btn>
        <Btn onClick={() => exec('italic')} title="Italic"><Italic size={16} /></Btn>
        <Btn onClick={() => exec('underline')} title="Underline"><Underline size={16} /></Btn>
        <span className="mx-1 h-5 w-px bg-line" />
        <Btn onClick={() => exec('formatBlock', 'H3')} title="Heading"><Heading size={16} /></Btn>
        <Btn onClick={() => exec('insertUnorderedList')} title="Bulleted list"><List size={16} /></Btn>
        <Btn onClick={() => exec('insertOrderedList')} title="Numbered list"><ListOrdered size={16} /></Btn>
        <span className="mx-1 h-5 w-px bg-line" />
        <Btn onClick={() => exec('removeFormat')} title="Clear formatting"><Eraser size={16} /></Btn>
      </div>
      <div
        ref={ref}
        contentEditable
        onInput={sync}
        data-placeholder={placeholder}
        className="prose-sm max-w-none min-h-[140px] px-3 py-2 text-sm bg-surface text-theme-primary focus:outline-none empty:before:content-[attr(data-placeholder)] empty:before:text-theme-secondary"
        suppressContentEditableWarning
      />
    </div>
  );
}
